import type { InsertCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface CreateCustomRoleTRPCInput {
  name: string;
  description?: string | null;
  roleKeys: string[];
  userIds: string[];
}

/**
 * Maps the tRPC `customRoleCreate` response back into the Apollo
 * `InsertCustomRoleMutation` shape so call sites can keep using the
 * existing `result.customRoleInsert?.Id` accessor regardless of which
 * branch ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertCustomRoleMutation => ({
  customRoleInsert: {
    __typename: 'IdOutput',
    Id: trpcData.Id,
  },
});

export const useCreateCustomRoleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customRole.customRoleCreate.mutationOptions({
      onSuccess: async () => {
        // Invalidate the custom-role list family — used by the settings
        // tab and the linked-items picker. The legacy Apollo path evicts
        // `custom_role` for the same purpose.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.role.getCustomRoles.queryKey(),
        });
      },
    }),
  });

  return {
    createCustomRole: async (
      variables: CreateCustomRoleTRPCInput
    ): Promise<InsertCustomRoleMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
